import React from 'react';
import InfoTip from './InfoTip';
import { INTERNAL_NOTES_LABEL, INTERNAL_NOTES_TOOLTIP } from '../constants/orderNotes';

/**
 * THE read-only rendering of `orders.admin_notes` — one component, three
 * surfaces: `pages/MyOrders.js`, `pages/Approvals.js`, and the non-manager
 * branch of `pages/DepartmentOrders.js`.
 *
 * 🔀 **EXTRACTED 2026-09-13, FROM THREE BYTE-IDENTICAL COPIES.** The three pages
 * each carried the same `<p><span>Admin Notes:</span> <span>{…}</span></p>`
 * inline. One copy would have been fine; three stopped being fine the moment
 * the label and a tooltip had to change together, which is exactly the Code
 * Unification Principle's case. Under the old markup a `check-duplication` group
 * never formed — the copies were JSX expressions inside three different
 * functions, not functions of their own — so the ratchet was never going to
 * catch this one. **The absence of a duplication group is not evidence that
 * there is no duplication.**
 *
 * 🔑 **IT RENDERS `null` FOR AN EMPTY NOTE, AND THE CALLER MUST *ALSO* GUARD.**
 * All three pages keep their own `detail.admin_notes ?` test before building the
 * node, and that is not belt-and-braces: `OrderExpandedRow` passes the finished
 * node into `OrderForm`'s `orderDetailsRow2Rest` slot, and an ELEMENT that renders
 * nothing is still truthy — the PHANTOM CHILD defect that left Admin Notes
 * 14rem wide on every `delivered` order without an ETA (owner report,
 * 2026-09-12, recorded at `expectedDeliveryNode` in `OrderExpandedRow.js`). The
 * guard here is defensive; the guard at the call site is the load-bearing one.
 *
 * ⚠️ **The `<p>` owns its own text colour and dark pair** — a shared
 * presentational component never inherits colour from an unknown parent
 * (`client/src/components/CLAUDE.md`; `ExpectedDeliveryBadge` read black on one
 * page and correct on another for exactly that reason).
 *
 * ⓘ The tooltip is the shared `components/InfoTip.js` in its documented idiom.
 * It sits as a SIBLING of the label span inside the paragraph rather than inside
 * a `<label>` — there is no form control here to label, and `InfoTip`'s own root
 * is `inline-flex … align-middle`, so it sits on the text baseline unaided.
 *
 * 🔀 `h-full` ADDED 2026-09-13, THIRD PASS — the row-2 grid cell it now sits
 * in (`orderDetailsRow2Rest`) stretches to match Expected Delivery's height
 * (CSS Grid's default `align-items: stretch`); a short paragraph stretched
 * taller than its own text is visually harmless, matching the editable
 * textarea's own `h-full` in `pages/DepartmentOrders.js`'s manager branch.
 */
export default function InternalNotesReadOnly({ notes }) {
  if (!notes) return null;
  return (
    <p className="h-full" data-testid="internal-notes-readonly">
      <span className="font-medium text-gray-700 dark:text-gray-300">{INTERNAL_NOTES_LABEL}:</span>
      {' '}
      <InfoTip title={INTERNAL_NOTES_TOOLTIP} />
      {' '}
      <span className="text-gray-600 dark:text-gray-400">{notes}</span>
    </p>
  );
}
